export interface ShopifyBridgeMarketCountry {
  code: string;
  name: string;
}

/**
 * Market details stored on a Shopify bridge `selected_markets` hash (GET and PATCH).
 * `catalog_id` and `catalog_status` may be absent on bridges persisted before those fields were added.
 */
export interface ShopifyBridgeMarketDetails {
  name: string;
  handle: string;
  catalog_id?: string;
  /** Import status for this market's catalog (mirrors BridgeStatus values) */
  catalog_status?: (typeof BridgeStatus)[keyof typeof BridgeStatus] | null;
  countries: ShopifyBridgeMarketCountry[];
  default_locale?: string;
}

/** Shopify Market GID => market details */
export type ShopifyBridgeSelectedMarkets = Record<string, ShopifyBridgeMarketDetails>;

export interface AlloysPartnersShopifyAppGroupIdBridgesBridgeIdResult {
  bridges: BridgeGetObject[];
}

export interface BridgeGetObject {
  id: string;
  external_id: string;
  status: (typeof BridgeStatus)[keyof typeof BridgeStatus];
  catalog_status: (typeof BridgeStatus)[keyof typeof BridgeStatus] | null;
  catalog_error_type: string | null;
  shop_name: string;
  processing: boolean;
  connected_at: string | null;
  disconnected_at: string | null;
  enabled_user_events: boolean | null;
  external_id_type: string | null;
  custom_id_endpoint: string | null;
  enabled_in_browser_messaging: boolean | null;
  email_opt_in: boolean | null;
  additional_email_subscription_group_ids: string[] | null;
  email_subscription_group_ids: string[] | null;
  sms_opt_in: boolean | null;
  sms_subscription_group: string | null;
  default_email_subscription_group_id: string | null;
  default_email_subscription_group_name: string | null;
  historical_backfill_opt_in: boolean | null;
  historical_backfill_status: (typeof BridgeStatus)[keyof typeof BridgeStatus] | null;
  historical_backfill_start_time: string | null;
  historical_backfill_end_time: string | null;
  historical_backfill_completion_time: string | null;
  historical_customer_backfill_status: (typeof BridgeStatus)[keyof typeof BridgeStatus] | null;
  historical_customer_backfill_start_time: string | null;
  historical_customer_backfill_end_time: string | null;
  historical_customer_backfill_completion_time: string | null;
  setup_completed_at: string | null;
  direct_integration_selected: boolean | null;
  third_party_tag_manager: boolean | null;
  user_tracking_behavior: string | null;
  catalog_opt_in: boolean | null;
  catalog_item_id_type: string | null;
  catalog_include_only_active_products: boolean | null;
  sync_product_tags?: boolean | null;
  selected_product_tags?: string[] | null;
  sync_product_metafields?: boolean | null;
  /** Hash: metafield id => { namespace, key, type? } */
  selected_product_metafields?: Record<string, { namespace: string; key: string; type?: string }> | null;
  has_all_access_scopes?: boolean | null;
  missing_access_scopes?: string[] | null;
  sync_product_collections?: boolean | null;
  /** Shopify collection GIDs */
  selected_collection_ids?: string[] | null;
  /** Collection GraphQL GID => Shopify title */
  selected_collections?: Record<string, string> | null;
  /** Shopify Market GID => market details (name, handle, catalog_id, catalog_status, countries, default_locale) */
  selected_markets?: ShopifyBridgeSelectedMarkets | null;
  app_embed_active: boolean | null;
}

export const BridgeStatus = {
  ACTIVE: 1,
  INACTIVE: 2,
  IN_PROGRESS: 3,
  STOPPED: 4,
} as const;

export interface AlloysPartnersShopifyAppGroupIdBridgesBridgeIdResult {}

export interface PatchAlloysPartnersShopifyAppGroupIdBridgesBridgeIdProps {
  /** The appGroupId that helps narrow down which bridge to update */
  appGroupId: string;
  /** The externalId that determines which bridge to update */
  bridgeId: string;
  /** Whether we are enabling user events or not */
  enabledUserEvents: boolean;

  /** External ID type selected */
  externalIdType?: string;

  /** Custom ID endpoint for retrieving external id */
  customIdEndpoint?: string;

  /** Whether we are enabling in browser messaging or not */
  enabledInBrowserMessaging: boolean;

  /** Whether we are enabling email subscription groups or not */
  emailOptIn: boolean;

  /** List of additional email subscription groups */
  additionalEmailSubscriptionGroupIds?: (string | undefined)[];

  /** Whether we are enabling sms subscription groups or not */
  smsOptIn: boolean;

  /** List of sms subscription groups */
  smsSubscriptionGroup?: string | undefined;

  /** Name for Email subscription group name created for this bridge */
  defaultEmailSubscriptionGroupName?: string;

  /** Whether we are enabling in historical backfill or not */
  historicalBackfillOptIn: boolean;

  /** Whether integration is direct or standard */
  directIntegrationSelected?: boolean;

  /** Whether the storefront implements the SDK with a tag manager */
  thirdPartyTagManager?: boolean;

  /** Standard path SDK user tracking behavior */
  userTrackingBehavior?: string;

  /** Whether we are enabling email catalog sync or not */
  catalogOptIn: boolean;

  /** Catalog item id type */
  catalogItemIdType?: string;

  /** Whether we are including only active products in catalog or not */
  catalogIncludeOnlyActiveProducts?: boolean;

  /** Whether to sync product tags */
  syncProductTags?: boolean;

  /** Product tags selected for syncing (sent as empty when syncProductTags is false) */
  selectedProductTags?: string[];

  /** Whether to sync product metafields */
  syncProductMetafields?: boolean;

  /** Product metafields selected for syncing: id => { namespace, key, type? } (sent as empty when syncProductMetafields is false) */
  selectedProductMetafields?: Record<string, { namespace: string; key: string; type?: string }>;

  /** Whether to sync product collections (collection_ids column) */
  syncProductCollections?: boolean;

  /** Shopify collection GIDs to sync (sent as empty when syncProductCollections is false) */
  selectedCollectionIds?: string[];

  /** Collection GID => title from Shopify (sent as empty when syncProductCollections is false) */
  selectedCollections?: Record<string, string>;

  /** Shopify Market GID => market details to sync */
  selectedMarkets?: ShopifyBridgeSelectedMarkets;
}

export interface DeleteAlloysPartnersShopifyAppGroupIdBridgesBridgeIdProps {
  appGroupId: string;
  bridgeId: string;
}
